import React, { useContext, useState, useMemo } from "react";
import { injectIntl } from "react-intl";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  FormGroup,
  Input,
  Row,
  UncontrolledDropdown,
} from "reactstrap";
import ReactTable from "react-table";
import "react-table/react-table.css";
import IntlMessages from "../../../helpers/IntlMessages";
import ShopeeWorkspaceContext from "./ShopeeWorkspaceContext";
import ShopeeV2Api from "../../../services/shopeeV2";
import "./ShopeeInventory.scss";

// Renders either a real provider value or an i18n message id fallback
const IdOrText = ({ value }) =>
  value && typeof value === "string" && value.startsWith("shopee.") ? (
    <IntlMessages id={value} />
  ) : (
    value || "—"
  );

// Utility formatting
const money = (val) => {
  if (val === null || val === undefined) return "0 đ";
  const num = Number(val);
  if (isNaN(num)) return "0 đ";
  if (num < 0) {
    return "-" + Math.abs(num).toLocaleString("vi-VN") + " đ";
  }
  return num.toLocaleString("vi-VN") + " đ";
};

// Channel Badge Component
const ChannelBadge = ({ channel }) => {
  switch (channel) {
    case "shopee":
      return (
        <span className="d-inline-flex align-items-center">
          <span
            style={{
              width: 18,
              height: 18,
              borderRadius: 4,
              backgroundColor: "#ee4d2d",
              color: "#fff",
              fontSize: 10,
              fontWeight: "bold",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              marginRight: 6,
            }}
          >
            S
          </span>
          Shopee
        </span>
      );
    case "tiktok":
      return (
        <span className="d-inline-flex align-items-center">
          <span
            style={{
              width: 18,
              height: 18,
              borderRadius: 4,
              backgroundColor: "#111",
              color: "#fff",
              fontSize: 10,
              fontWeight: "bold",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              marginRight: 6,
            }}
          >
            🎵
          </span>
          TikTok Shop
        </span>
      );
    case "lazada":
      return (
        <span className="d-inline-flex align-items-center">
          <span
            style={{
              width: 18,
              height: 18,
              borderRadius: 4,
              backgroundColor: "#0f146d",
              color: "#fff",
              fontSize: 10,
              fontWeight: "bold",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              marginRight: 6,
            }}
          >
            L
          </span>
          Lazada
        </span>
      );
    case "website":
      return (
        <span className="d-inline-flex align-items-center">
          <span
            style={{
              width: 18,
              height: 18,
              borderRadius: 4,
              backgroundColor: "#0284c7",
              color: "#fff",
              fontSize: 10,
              fontWeight: "bold",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              marginRight: 6,
            }}
          >
            🌐
          </span>
          Website
        </span>
      );
    case "facebook":
      return (
        <span className="d-inline-flex align-items-center">
          <span
            style={{
              width: 18,
              height: 18,
              borderRadius: 4,
              backgroundColor: "#1877f2",
              color: "#fff",
              fontSize: 10,
              fontWeight: "bold",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              marginRight: 6,
            }}
          >
            f
          </span>
          Facebook Shop
        </span>
      );
    case "pos":
      return (
        <span className="d-inline-flex align-items-center">
          <span
            style={{
              width: 18,
              height: 18,
              borderRadius: 4,
              backgroundColor: "#7c3aed",
              color: "#fff",
              fontSize: 10,
              fontWeight: "bold",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              marginRight: 6,
            }}
          >
            🏪
          </span>
          <IntlMessages id="shopee.workspace.finance.channel-pos" />
        </span>
      );
    default:
      return <span>{channel}</span>;
  }
};

// Status Badge Component (code-based; labels resolved via i18n)
const StatusBadge = ({ status }) => {
  const labelId = {
    completed: "shopee.workspace.finance.status.paid",
    released: "shopee.workspace.finance.status.paid",
    pending: "shopee.workspace.finance.status.pending-payment",
    discrepancy: "shopee.workspace.finance.status.discrepancy",
    dispute: "shopee.workspace.finance.status.dispute",
  }[status];
  const style = {
    completed: {
      backgroundColor: "#e6f4ea",
      color: "#137333",
      border: "1px solid #ceedd5",
    },
    released: {
      backgroundColor: "#e6f4ea",
      color: "#137333",
      border: "1px solid #ceedd5",
    },
    pending: {
      backgroundColor: "#fef7e0",
      color: "#b06000",
      border: "1px solid #fde293",
    },
    discrepancy: {
      backgroundColor: "#fce8e6",
      color: "#c5221f",
      border: "1px solid #fad2cf",
    },
    dispute: {
      backgroundColor: "#f3e8ff",
      color: "#7e22ce",
      border: "1px solid #e9d5ff",
    },
  }[status];

  if (!labelId) return <Badge color="secondary">{status}</Badge>;
  return (
    <Badge
      color="light"
      style={{
        ...style,
        borderRadius: 12,
        padding: "4px 8px",
        fontSize: 11,
      }}
    >
      <IntlMessages id={labelId} />
    </Badge>
  );
};

export default injectIntl(function ShopeeFinancePanel({ intl }) {
  const workspace = useContext(ShopeeWorkspaceContext);
  const writable = workspace ? workspace.capabilities?.shopee_finance : true;

  // Filter & State management
  const [activeTab, setActiveTab] = useState("all");
  const [selectedChannel, setSelectedChannel] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedBank, setSelectedBank] = useState("all");
  const [dateRange, setDateRange] = useState("01/07/2024 - 31/07/2024");
  const [selectedSettlement, setSelectedSettlement] = useState(null);
  const [detailTab, setDetailTab] = useState("overview");

  // Real workspace settlement rows
  const settlements = useMemo(() => {
    const rawList = workspace?.data?.settlements || [];
    return rawList.map((item, idx) => ({
      id: item.id || idx + 1,
      settlement_sn: item.settlement_sn || `ST-${item.id || idx + 1}`,
      period:
        item.period ||
        (item.created_at
          ? new Date(item.created_at).toLocaleDateString("vi-VN")
          : "—"),
      channel: "shopee",
      orderCount: item.order_count || 1,
      grossAmount: Number(item.gross_amount || item.total_amount || 0),
      feeAmount: -Math.abs(Number(item.commission_fee || item.fee || 0)),
      refundAmount: -Math.abs(Number(item.refund_amount || 0)),
      netPayout: Number(item.net_payout || item.amount || 0),
      payoutDate:
        item.payout_date ||
        (item.updatedAt
          ? new Date(item.updatedAt).toLocaleDateString("vi-VN")
          : "—"),
      status:
        item.payout_status === "released" || item.status === "COMPLETED"
          ? "completed"
          : "pending",
      bankAccount:
        item.bank_account || "shopee.workspace.finance.bank-encrypted",
      transactionCode: item.transaction_code || item.transaction_id || "—",
      payoutStatusText:
        item.payout_status === "released"
          ? "shopee.workspace.finance.payout.transferred"
          : "shopee.workspace.finance.payout.processing",
      platformDiscount: -Math.abs(Number(item.platform_discount || 0)),
      fees: -Math.abs(Number(item.commission_fee || item.fee || 0)),
      refunds: -Math.abs(Number(item.refund_amount || 0)),
      discrepancyAmount: Number(item.discrepancy_amount || 0),
      discrepancyOrdersCount: Number(item.discrepancy_orders || 0),
    }));
  }, [workspace?.data?.settlements]);

  // Filtered rows
  const filteredRows = useMemo(() => {
    return settlements.filter((item) => {
      // Tab filter
      if (activeTab === "pending" && item.status !== "pending") return false;
      if (activeTab === "completed" && item.status !== "completed")
        return false;
      if (activeTab === "refund" && item.refundAmount >= 0) return false;
      if (activeTab === "dispute" && item.status !== "dispute") return false;
      if (activeTab === "discrepancy" && item.status !== "discrepancy")
        return false;

      // Dropdown filters
      if (selectedChannel !== "all" && item.channel !== selectedChannel)
        return false;
      if (selectedStatus !== "all") {
        if (selectedStatus !== item.status) return false;
      }
      if (selectedBank !== "all" && !item.bankAccount.includes(selectedBank))
        return false;

      return true;
    });
  }, [settlements, activeTab, selectedChannel, selectedStatus, selectedBank]);

  // Dynamic KPI calculations
  const kpiData = useMemo(() => {
    let revenue = 0;
    let paid = 0;
    let pending = 0;
    let refund = 0;
    let fee = 0;

    settlements.forEach((item) => {
      revenue += item.grossAmount || 0;
      if (item.status === "completed") {
        paid += item.netPayout || 0;
      } else {
        pending += item.netPayout || 0;
      }
      refund += Math.abs(item.refundAmount || 0);
      fee += Math.abs(item.feeAmount || 0);
    });

    return {
      revenue: money(revenue),
      paid: money(paid),
      pending: money(pending),
      holding: money(0),
      refund: money(refund),
      fee: money(fee),
    };
  }, [settlements]);

  // Sync action handler
  const handleSyncFinance = () => {
    if (workspace?.runAction) {
      workspace.runAction(
        "sync-settlements",
        (key) =>
          ShopeeV2Api.syncSettlements(workspace.selectedShopId, null, key),
        intl.formatMessage({ id: "shopee.workspace.finance.sync-queued" }),
      );
    }
  };

  return (
    <div
      className="shopee-finance-panel pb-5"
      style={{ backgroundColor: "#f8fafc", minHeight: "100vh" }}
    >
      {/* 1. Header Action Bar (Without left sidebar & top outer header) */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4">
        <div>
          <h4
            className="font-weight-bold text-dark mb-1"
            style={{ fontSize: 22 }}
          >
            <IntlMessages id="shopee.workspace.tab.reconciliation" />
          </h4>
          <p className="text-muted mb-0 small">
            <IntlMessages id="shopee.workspace.finance.subtitle" />
          </p>
        </div>
        <div
          className="d-flex align-items-center mt-3 mt-md-0 flex-wrap"
          style={{ gap: 8 }}
        >
          <Button
            outline
            color="secondary"
            size="sm"
            className="bg-white text-dark border d-flex align-items-center"
            style={{ borderRadius: 6, fontWeight: 500 }}
          >
            <span className="mr-1">📊</span>{" "}
            <IntlMessages id="shopee.workspace.finance.forecast" />{" "}
            <Badge
              color="danger"
              className="ml-1 px-1"
              style={{ fontSize: 9, borderRadius: 4 }}
            >
              Beta
            </Badge>
          </Button>
          <Button
            outline
            color="secondary"
            size="sm"
            className="bg-white text-dark border d-flex align-items-center"
            style={{ borderRadius: 6, fontWeight: 500 }}
          >
            <span className="mr-1">🤖</span> AI Insight
          </Button>
          <Button
            outline
            color="secondary"
            size="sm"
            className="bg-white text-dark border d-flex align-items-center"
            style={{ borderRadius: 6, fontWeight: 500 }}
          >
            <span className="mr-1">📥</span>{" "}
            <IntlMessages id="shopee.workspace.finance.export-report" />
          </Button>
          {writable && (
            <Button
              color="primary"
              size="sm"
              style={{
                backgroundColor: "#0066FE",
                borderRadius: 6,
                fontWeight: 600,
              }}
              onClick={handleSyncFinance}
            >
              <IntlMessages id="shopee.workspace.finance.action.sync" />
            </Button>
          )}
        </div>
      </div>

      {/* 2. Top KPI Summary Cards Row (6 Cards) */}
      <Row className="mb-4">
        {/* KPI 1: Doanh thu */}
        <Col xs="12" sm="6" md="4" lg="2" className="mb-3 mb-lg-0">
          <Card
            className="border-0 shadow-sm h-100"
            style={{ borderRadius: 10 }}
          >
            <CardBody className="p-3">
              <div className="d-flex align-items-center mb-2">
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center mr-2"
                  style={{
                    width: 32,
                    height: 32,
                    backgroundColor: "#e6f4ea",
                    color: "#137333",
                  }}
                >
                  💵
                </div>
                <span className="small text-muted font-weight-bold">
                  <IntlMessages id="shopee.workspace.finance.kpi.revenue" />
                </span>
              </div>
              <h5
                className="font-weight-bold text-dark mb-1"
                style={{ fontSize: 16 }}
              >
                {kpiData.revenue}
              </h5>
              <div className="small text-success" style={{ fontSize: 11 }}>
                ▲ 12.5%{" "}
                <span className="text-muted">
                  <IntlMessages id="shopee.workspace.finance.kpi.vs-prev-period" />
                </span>
              </div>
            </CardBody>
          </Card>
        </Col>

        {/* KPI 2: Đã thanh toán */}
        <Col xs="12" sm="6" md="4" lg="2" className="mb-3 mb-lg-0">
          <Card
            className="border-0 shadow-sm h-100"
            style={{ borderRadius: 10 }}
          >
            <CardBody className="p-3">
              <div className="d-flex align-items-center mb-2">
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center mr-2"
                  style={{
                    width: 32,
                    height: 32,
                    backgroundColor: "#e6f4ea",
                    color: "#137333",
                  }}
                >
                  ✓
                </div>
                <span className="small text-muted font-weight-bold">
                  <IntlMessages id="shopee.workspace.finance.status.paid" />
                </span>
              </div>
              <h5
                className="font-weight-bold text-dark mb-1"
                style={{ fontSize: 16 }}
              >
                {kpiData.paid}
              </h5>
              <div className="small text-success" style={{ fontSize: 11 }}>
                ▲ 10.3%
              </div>
            </CardBody>
          </Card>
        </Col>

        {/* KPI 3: Chờ đối soát */}
        <Col xs="12" sm="6" md="4" lg="2" className="mb-3 mb-lg-0">
          <Card
            className="border-0 shadow-sm h-100"
            style={{ borderRadius: 10 }}
          >
            <CardBody className="p-3">
              <div className="d-flex align-items-center mb-2">
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center mr-2"
                  style={{
                    width: 32,
                    height: 32,
                    backgroundColor: "#fef3c7",
                    color: "#d97706",
                  }}
                >
                  🕒
                </div>
                <span className="small text-muted font-weight-bold">
                  <IntlMessages id="shopee.workspace.finance.kpi.pending-reconciliation" />
                </span>
              </div>
              <h5
                className="font-weight-bold text-dark mb-1"
                style={{ fontSize: 16 }}
              >
                {kpiData.pending}
              </h5>
              <div
                className="small text-warning"
                style={{ fontSize: 11, color: "#d97706" }}
              >
                ▲ 4.2%
              </div>
            </CardBody>
          </Card>
        </Col>

        {/* KPI 4: Đang giữ */}
        <Col xs="12" sm="6" md="4" lg="2" className="mb-3 mb-lg-0">
          <Card
            className="border-0 shadow-sm h-100"
            style={{ borderRadius: 10 }}
          >
            <CardBody className="p-3">
              <div className="d-flex align-items-center mb-2">
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center mr-2"
                  style={{
                    width: 32,
                    height: 32,
                    backgroundColor: "#ffedd5",
                    color: "#c2410c",
                  }}
                >
                  🔒
                </div>
                <span className="small text-muted font-weight-bold">
                  <IntlMessages id="shopee.workspace.finance.kpi.holding" />
                </span>
              </div>
              <h5
                className="font-weight-bold text-dark mb-1"
                style={{ fontSize: 16 }}
              >
                {kpiData.holding}
              </h5>
              <div className="small" style={{ fontSize: 11, color: "#c2410c" }}>
                ▲ 2.8%
              </div>
            </CardBody>
          </Card>
        </Col>

        {/* KPI 5: Hoàn tiền */}
        <Col xs="12" sm="6" md="4" lg="2" className="mb-3 mb-lg-0">
          <Card
            className="border-0 shadow-sm h-100"
            style={{ borderRadius: 10 }}
          >
            <CardBody className="p-3">
              <div className="d-flex align-items-center mb-2">
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center mr-2"
                  style={{
                    width: 32,
                    height: 32,
                    backgroundColor: "#fee2e2",
                    color: "#dc2626",
                  }}
                >
                  ↩️
                </div>
                <span className="small text-muted font-weight-bold">
                  <IntlMessages id="shopee.workspace.finance.kpi.refund" />
                </span>
              </div>
              <h5
                className="font-weight-bold text-dark mb-1"
                style={{ fontSize: 16 }}
              >
                {kpiData.refund}
              </h5>
              <div className="small text-danger" style={{ fontSize: 11 }}>
                ▼ 8.6%
              </div>
            </CardBody>
          </Card>
        </Col>

        {/* KPI 6: Phí & Khấu trừ */}
        <Col xs="12" sm="6" md="4" lg="2" className="mb-3 mb-lg-0">
          <Card
            className="border-0 shadow-sm h-100"
            style={{ borderRadius: 10 }}
          >
            <CardBody className="p-3">
              <div className="d-flex align-items-center mb-2">
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center mr-2"
                  style={{
                    width: 32,
                    height: 32,
                    backgroundColor: "#e0f2fe",
                    color: "#0284c7",
                  }}
                >
                  %
                </div>
                <span className="small text-muted font-weight-bold">
                  <IntlMessages id="shopee.workspace.finance.kpi.fees" />
                </span>
              </div>
              <h5
                className="font-weight-bold text-dark mb-1"
                style={{ fontSize: 16 }}
              >
                {kpiData.fee}
              </h5>
              <div className="small text-info" style={{ fontSize: 11 }}>
                ▼ 3.7%
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* 3. Middle Section: Cash Flow Multi-Line Chart & Payout Forecast */}
      <Row className="mb-4">
        {/* Cash Flow Line Chart Card (Col 8) */}
        <Col xs="12" lg="8" className="mb-3 mb-lg-0">
          <Card
            className="border-0 shadow-sm h-100"
            style={{ borderRadius: 10 }}
          >
            <CardBody className="p-3">
              <div className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center mb-3">
                <h6
                  className="font-weight-bold text-dark mb-2 mb-sm-0"
                  style={{ fontSize: 15 }}
                >
                  <IntlMessages id="shopee.workspace.finance.cashflow-title" />
                </h6>
                <div
                  className="d-flex align-items-center flex-wrap"
                  style={{ gap: 12, fontSize: 12 }}
                >
                  <span className="d-flex align-items-center">
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: "#10b981",
                        marginRight: 5,
                      }}
                    ></span>
                    <IntlMessages id="shopee.workspace.finance.kpi.revenue" />
                  </span>
                  <span className="d-flex align-items-center">
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: "#0066FE",
                        marginRight: 5,
                      }}
                    ></span>
                    <IntlMessages id="shopee.workspace.finance.net-received" />
                  </span>
                  <span className="d-flex align-items-center">
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: "#f59e0b",
                        marginRight: 5,
                      }}
                    ></span>
                    <IntlMessages id="shopee.workspace.finance.kpi.refund" />
                  </span>
                  <span className="d-flex align-items-center">
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: "#8b5cf6",
                        marginRight: 5,
                      }}
                    ></span>
                    <IntlMessages id="shopee.workspace.finance.kpi.fees" />
                  </span>
                </div>
              </div>

              {/* Dynamic SVG Line Chart Component */}
              <div style={{ position: "relative", height: 200, width: "100%" }}>
                <svg
                  width="100%"
                  height="100%"
                  viewBox="0 0 700 180"
                  preserveAspectRatio="none"
                >
                  {/* Grid Lines */}
                  <line
                    x1="40"
                    y1="20"
                    x2="680"
                    y2="20"
                    stroke="#f1f5f9"
                    strokeWidth="1"
                  />
                  <line
                    x1="40"
                    y1="60"
                    x2="680"
                    y2="60"
                    stroke="#f1f5f9"
                    strokeWidth="1"
                  />
                  <line
                    x1="40"
                    y1="100"
                    x2="680"
                    y2="100"
                    stroke="#f1f5f9"
                    strokeWidth="1"
                  />
                  <line
                    x1="40"
                    y1="140"
                    x2="680"
                    y2="140"
                    stroke="#e2e8f0"
                    strokeWidth="1"
                  />

                  {/* Y-Axis Labels */}
                  <text x="0" y="25" fill="#94a3b8" fontSize="10">
                    120tr
                  </text>
                  <text x="5" y="65" fill="#94a3b8" fontSize="10">
                    90tr
                  </text>
                  <text x="5" y="105" fill="#94a3b8" fontSize="10">
                    60tr
                  </text>
                  <text x="5" y="145" fill="#94a3b8" fontSize="10">
                    30tr
                  </text>
                  <text x="20" y="175" fill="#94a3b8" fontSize="10">
                    0
                  </text>

                  {/* Line 1: Doanh thu (Green) */}
                  <path
                    d="M 60 70 C 120 40, 180 60, 240 30 C 300 45, 360 25, 420 30 C 480 35, 540 60, 600 40 C 630 30, 660 25, 680 20"
                    fill="none"
                    stroke="#10b981"
                    strokeWidth="2.5"
                  />
                  {/* Doanh thu dots */}
                  <circle cx="60" cy="70" r="3.5" fill="#10b981" />
                  <circle cx="160" cy="50" r="3.5" fill="#10b981" />
                  <circle cx="260" cy="30" r="3.5" fill="#10b981" />
                  <circle cx="360" cy="25" r="3.5" fill="#10b981" />
                  <circle cx="460" cy="35" r="3.5" fill="#10b981" />
                  <circle cx="560" cy="50" r="3.5" fill="#10b981" />
                  <circle cx="660" cy="22" r="3.5" fill="#10b981" />

                  {/* Line 2: Thực nhận (Blue) */}
                  <path
                    d="M 60 90 C 120 70, 180 80, 240 50 C 300 65, 360 48, 420 50 C 480 65, 540 85, 600 60 C 630 50, 660 45, 680 40"
                    fill="none"
                    stroke="#0066FE"
                    strokeWidth="2.5"
                  />
                  <circle cx="60" cy="90" r="3.5" fill="#0066FE" />
                  <circle cx="160" cy="73" r="3.5" fill="#0066FE" />
                  <circle cx="260" cy="50" r="3.5" fill="#0066FE" />
                  <circle cx="360" cy="48" r="3.5" fill="#0066FE" />
                  <circle cx="460" cy="65" r="3.5" fill="#0066FE" />
                  <circle cx="560" cy="75" r="3.5" fill="#0066FE" />
                  <circle cx="660" cy="42" r="3.5" fill="#0066FE" />

                  {/* Line 3: Hoàn tiền (Orange) */}
                  <path
                    d="M 60 135 C 120 125, 180 130, 240 120 C 300 130, 360 122, 420 115 C 480 125, 540 120, 600 130 C 630 125, 660 135, 680 130"
                    fill="none"
                    stroke="#f59e0b"
                    strokeWidth="2"
                  />

                  {/* Line 4: Phí & khấu trừ (Purple) */}
                  <path
                    d="M 60 125 C 120 115, 180 120, 240 110 C 300 118, 360 112, 420 105 C 480 115, 540 110, 600 120 C 630 115, 660 122, 680 118"
                    fill="none"
                    stroke="#8b5cf6"
                    strokeWidth="2"
                  />

                  {/* X-Axis Dates Labels */}
                  <text
                    x="50"
                    y="172"
                    fill="#64748b"
                    fontSize="10"
                    fontWeight="500"
                  >
                    16/07
                  </text>
                  <text
                    x="150"
                    y="172"
                    fill="#64748b"
                    fontSize="10"
                    fontWeight="500"
                  >
                    17/07
                  </text>
                  <text
                    x="250"
                    y="172"
                    fill="#64748b"
                    fontSize="10"
                    fontWeight="500"
                  >
                    18/07
                  </text>
                  <text
                    x="350"
                    y="172"
                    fill="#64748b"
                    fontSize="10"
                    fontWeight="500"
                  >
                    19/07
                  </text>
                  <text
                    x="450"
                    y="172"
                    fill="#64748b"
                    fontSize="10"
                    fontWeight="500"
                  >
                    20/07
                  </text>
                  <text
                    x="550"
                    y="172"
                    fill="#64748b"
                    fontSize="10"
                    fontWeight="500"
                  >
                    21/07
                  </text>
                  <text
                    x="650"
                    y="172"
                    fill="#64748b"
                    fontSize="10"
                    fontWeight="500"
                  >
                    22/07
                  </text>
                </svg>
              </div>
            </CardBody>
          </Card>
        </Col>

        {/* Payout Forecast Box (Col 4) */}
        <Col xs="12" lg="4">
          <Card
            className="border-0 shadow-sm h-100"
            style={{ borderRadius: 10 }}
          >
            <CardBody className="p-3 d-flex flex-column justify-content-between">
              <div>
                <h6
                  className="font-weight-bold text-dark mb-3"
                  style={{ fontSize: 15 }}
                >
                  <IntlMessages id="shopee.workspace.finance.forecast" />
                </h6>
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                    <span className="text-muted small">
                      <IntlMessages id="shopee.workspace.overview.period.today" />
                    </span>
                    <span
                      className="font-weight-bold text-success"
                      style={{ fontSize: 15 }}
                    >
                      25.500.000 đ
                    </span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                    <span className="text-muted small">
                      <IntlMessages id="shopee.workspace.finance.forecast.tomorrow" />
                    </span>
                    <span
                      className="font-weight-bold text-success"
                      style={{ fontSize: 15 }}
                    >
                      42.000.000 đ
                    </span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                    <span className="text-muted small">
                      <IntlMessages id="shopee.workspace.finance.forecast.next-3-days" />
                    </span>
                    <span
                      className="font-weight-bold text-success"
                      style={{ fontSize: 15 }}
                    >
                      65.000.000 đ
                    </span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="text-muted small">
                      <IntlMessages id="shopee.workspace.finance.forecast.next-week" />
                    </span>
                    <span
                      className="font-weight-bold text-success"
                      style={{ fontSize: 15 }}
                    >
                      180.000.000 đ
                    </span>
                  </div>
                </div>
              </div>
              <div className="text-center pt-2">
                <Button
                  color="link"
                  className="p-0 text-primary small font-weight-bold text-decoration-none"
                >
                  <IntlMessages id="shopee.workspace.finance.forecast.view-detail" />
                </Button>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* 4. Tab Navigation & Advanced Filter Controls */}
      <div className="mb-3">
        {/* Status Tabs Bar */}
        <div
          className="d-flex align-items-center flex-wrap mb-3 border-bottom pb-2"
          style={{ gap: 6 }}
        >
          {[
            {
              id: "all",
              labelId: "shopee.workspace.catalog.channel-all",
              count: 128,
              color: "#0066FE",
              bg: "#eff6ff",
            },
            {
              id: "pending",
              labelId: "shopee.workspace.finance.kpi.pending-reconciliation",
              count: 18,
              color: "#dc2626",
              bg: "#fef2f2",
            },
            {
              id: "completed",
              labelId: "shopee.workspace.finance.status.paid",
              count: 86,
              color: "#0066FE",
              bg: "#eff6ff",
            },
            {
              id: "refund",
              labelId: "shopee.workspace.finance.kpi.refund",
              count: 12,
              color: "#dc2626",
              bg: "#fef2f2",
            },
            {
              id: "dispute",
              labelId: "shopee.workspace.finance.status.dispute",
              count: 7,
              color: "#0066FE",
              bg: "#eff6ff",
            },
            {
              id: "fee",
              labelId: "shopee.workspace.finance.kpi.fees",
              count: 23,
              color: "#64748b",
              bg: "#f1f5f9",
            },
            {
              id: "discrepancy",
              labelId: "shopee.workspace.finance.status.discrepancy",
              count: 9,
              color: "#64748b",
              bg: "#f1f5f9",
            },
          ].map((tab) => (
            <Button
              key={tab.id}
              color="link"
              className={`p-2 text-decoration-none d-flex align-items-center ${
                activeTab === tab.id
                  ? "font-weight-bold text-dark border-bottom border-primary"
                  : "text-muted"
              }`}
              style={{
                borderRadius: 0,
                fontSize: 13,
                borderBottomWidth: activeTab === tab.id ? 2 : 0,
              }}
              onClick={() => setActiveTab(tab.id)}
            >
              <IntlMessages id={tab.labelId} />{" "}
              <Badge
                className="ml-1 px-2 py-1"
                style={{
                  backgroundColor: tab.bg,
                  color: tab.color,
                  borderRadius: 10,
                  fontSize: 11,
                  fontWeight: 600,
                }}
              >
                {tab.count}
              </Badge>
            </Button>
          ))}
        </div>

        {/* Filter Input Dropdowns */}
        <Row className="align-items-center mb-3">
          <Col xs="12" md="3" className="mb-2 mb-md-0">
            <div className="position-relative">
              <Input
                type="text"
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                bsSize="sm"
                className="bg-white border rounded"
                style={{ height: 36, paddingLeft: 30 }}
              />
              <span
                className="position-absolute text-muted"
                style={{ left: 10, top: 8, fontSize: 13 }}
              >
                📅
              </span>
            </div>
          </Col>

          <Col xs="6" sm="3" md="2" className="mb-2 mb-md-0">
            <Input
              type="select"
              bsSize="sm"
              value={selectedChannel}
              onChange={(e) => setSelectedChannel(e.target.value)}
              className="bg-white border rounded"
              style={{ height: 36 }}
            >
              <option value="all">
                <IntlMessages id="shopee.workspace.common.all-channels" />
              </option>
              <option value="shopee">Shopee</option>
              <option value="tiktok">TikTok Shop</option>
              <option value="lazada">Lazada</option>
              <option value="website">Website</option>
              <option value="facebook">Facebook Shop</option>
              <option value="pos">
                <IntlMessages id="shopee.workspace.finance.channel-pos" />
              </option>
            </Input>
          </Col>

          <Col xs="6" sm="3" md="2" className="mb-2 mb-md-0">
            <Input
              type="select"
              bsSize="sm"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="bg-white border rounded"
              style={{ height: 36 }}
            >
              <option value="all">
                <IntlMessages id="shopee.workspace.common.all-statuses" />
              </option>
              <option value="completed">
                <IntlMessages id="shopee.workspace.finance.status.paid" />
              </option>
              <option value="pending">
                <IntlMessages id="shopee.workspace.finance.status.pending-payment" />
              </option>
              <option value="discrepancy">
                <IntlMessages id="shopee.workspace.finance.status.discrepancy" />
              </option>
              <option value="dispute">
                <IntlMessages id="shopee.workspace.finance.status.dispute" />
              </option>
            </Input>
          </Col>

          <Col xs="6" sm="3" md="3" className="mb-2 mb-md-0">
            <Input
              type="select"
              bsSize="sm"
              value={selectedBank}
              onChange={(e) => setSelectedBank(e.target.value)}
              className="bg-white border rounded"
              style={{ height: 36 }}
            >
              <option value="all">
                <IntlMessages id="shopee.workspace.finance.filter.all-banks" />
              </option>
              <option value="MB Bank">MB Bank - 1234 5678 9999</option>
              <option value="Techcombank">Techcombank - 8888 9999 11</option>
              <option value="Vietcombank">Vietcombank - 0011 2233 4455</option>
            </Input>
          </Col>

          <Col xs="6" sm="3" md="2">
            <Button
              outline
              color="secondary"
              bsSize="sm"
              className="w-100 bg-white border text-dark d-flex align-items-center justify-content-center"
              style={{ height: 36, borderRadius: 6, fontWeight: 500 }}
            >
              <span className="mr-1">🎛️</span>{" "}
              <IntlMessages id="shopee.workspace.common.filter" />
            </Button>
          </Col>
        </Row>
      </div>

      {/* 5. Main Content: Table + Side Drawer (If drawer open) */}
      <Row className="mb-4">
        <Col
          xs="12"
          lg={selectedSettlement ? "8" : "12"}
          className="transition-all"
        >
          <Card className="border-0 shadow-sm" style={{ borderRadius: 10 }}>
            <CardBody className="p-0">
              <ReactTable
                data={filteredRows}
                columns={[
                  {
                    Header: "",
                    accessor: "id",
                    width: 36,
                    Cell: () => (
                      <Input
                        type="checkbox"
                        className="position-relative"
                        style={{ margin: "0 auto" }}
                      />
                    ),
                  },
                  {
                    Header: (
                      <IntlMessages id="shopee.workspace.finance.col.sn" />
                    ),
                    accessor: "settlement_sn",
                    minWidth: 120,
                    Cell: (row) => (
                      <span
                        className="text-primary font-weight-bold cursor-pointer"
                        style={{ cursor: "pointer" }}
                        onClick={() => setSelectedSettlement(row.original)}
                      >
                        {row.value}
                      </span>
                    ),
                  },
                  {
                    Header: (
                      <IntlMessages id="shopee.workspace.finance.col.period" />
                    ),
                    accessor: "period",
                    minWidth: 140,
                    Cell: (row) => (
                      <span className="small text-muted">{row.value}</span>
                    ),
                  },
                  {
                    Header: (
                      <IntlMessages id="shopee.workspace.returns.col.channel" />
                    ),
                    accessor: "channel",
                    minWidth: 120,
                    Cell: (row) => <ChannelBadge channel={row.value} />,
                  },
                  {
                    Header: (
                      <IntlMessages id="shopee.workspace.finance.col.order-count" />
                    ),
                    accessor: "orderCount",
                    width: 70,
                    Cell: (row) => (
                      <span className="font-weight-bold">{row.value}</span>
                    ),
                  },
                  {
                    Header: (
                      <IntlMessages id="shopee.workspace.finance.kpi.revenue" />
                    ),
                    accessor: "grossAmount",
                    minWidth: 110,
                    Cell: (row) => (
                      <span className="font-weight-bold">
                        {money(row.value)}
                      </span>
                    ),
                  },
                  {
                    Header: (
                      <IntlMessages id="shopee.workspace.finance.kpi.fees" />
                    ),
                    accessor: "feeAmount",
                    minWidth: 110,
                    Cell: (row) => (
                      <span className="text-danger">{money(row.value)}</span>
                    ),
                  },
                  {
                    Header: (
                      <IntlMessages id="shopee.workspace.finance.kpi.refund" />
                    ),
                    accessor: "refundAmount",
                    minWidth: 100,
                    Cell: (row) => (
                      <span className="text-danger">{money(row.value)}</span>
                    ),
                  },
                  {
                    Header: (
                      <IntlMessages id="shopee.workspace.finance.net-received" />
                    ),
                    accessor: "netPayout",
                    minWidth: 120,
                    Cell: (row) => (
                      <span className="font-weight-bold text-dark">
                        {money(row.value)}
                      </span>
                    ),
                  },
                  {
                    Header: (
                      <IntlMessages id="shopee.workspace.finance.col.payout-date" />
                    ),
                    accessor: "payoutDate",
                    minWidth: 110,
                    Cell: (row) => (
                      <span className="small text-muted">{row.value}</span>
                    ),
                  },
                  {
                    Header: (
                      <IntlMessages id="shopee.workspace.returns.col.status" />
                    ),
                    accessor: "status",
                    minWidth: 120,
                    Cell: (row) => <StatusBadge status={row.value} />,
                  },
                  {
                    Header: "",
                    accessor: "id",
                    width: 45,
                    Cell: (row) => (
                      <UncontrolledDropdown>
                        <DropdownToggle
                          color="link"
                          size="sm"
                          className="p-0 text-muted"
                        >
                          •••
                        </DropdownToggle>
                        <DropdownMenu right>
                          <DropdownItem
                            onClick={() => setSelectedSettlement(row.original)}
                          >
                            <IntlMessages id="shopee.workspace.returns.view-detail-title" />
                          </DropdownItem>
                          <DropdownItem>
                            <IntlMessages id="shopee.workspace.finance.action.export-excel-file" />
                          </DropdownItem>
                        </DropdownMenu>
                      </UncontrolledDropdown>
                    ),
                  },
                ]}
                defaultPageSize={8}
                className="-striped -highlight border-0"
                style={{ fontSize: 13 }}
                previousText="‹"
                nextText="›"
                rowsText={intl.formatMessage({
                  id: "shopee.workspace.common.rows",
                })}
                pageText={intl.formatMessage({
                  id: "shopee.workspace.common.page",
                })}
                ofText={intl.formatMessage({
                  id: "shopee.workspace.common.of",
                })}
              />
            </CardBody>
          </Card>
        </Col>

        {/* 6. Right Side Detail Drawer (Chi tiết đối soát) */}
        {selectedSettlement && (
          <Col xs="12" lg="4" className="mt-3 mt-lg-0">
            <Card
              className="border-0 shadow-sm h-100"
              style={{ borderRadius: 10 }}
            >
              <CardBody className="p-3 position-relative">
                {/* Header & Back button */}
                <div className="d-flex justify-content-between align-items-center mb-2 border-bottom pb-2">
                  <div className="d-flex align-items-center">
                    <span
                      className="text-muted cursor-pointer mr-2 font-weight-bold"
                      style={{ cursor: "pointer" }}
                      onClick={() => setSelectedSettlement(null)}
                    >
                      ‹
                    </span>
                    <span className="small text-muted">
                      <IntlMessages id="shopee.workspace.finance.detail-title" />
                    </span>
                  </div>
                  <Button
                    color="link"
                    className="p-0 text-muted font-weight-bold text-decoration-none"
                    onClick={() => setSelectedSettlement(null)}
                  >
                    ✕
                  </Button>
                </div>

                {/* ID & Status badge header */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="d-flex align-items-center">
                    <h5
                      className="font-weight-bold text-dark mb-0 mr-2"
                      style={{ fontSize: 18 }}
                    >
                      {selectedSettlement.settlement_sn}
                    </h5>
                    <StatusBadge status={selectedSettlement.status} />
                  </div>
                  <ChannelBadge channel={selectedSettlement.channel} />
                </div>

                {/* Metadata details block */}
                <div
                  className="small text-muted mb-3 bg-light p-2 rounded"
                  style={{ fontSize: 12 }}
                >
                  <div className="d-flex justify-content-between mb-1">
                    <span>
                      <IntlMessages id="shopee.workspace.finance.meta.period" />
                    </span>
                    <strong className="text-dark">
                      {selectedSettlement.period}
                    </strong>
                  </div>
                  <div className="d-flex justify-content-between mb-1">
                    <span>
                      <IntlMessages id="shopee.workspace.finance.meta.payout-date" />
                    </span>
                    <strong className="text-dark">
                      {selectedSettlement.payoutDate}
                    </strong>
                  </div>
                  <div className="d-flex justify-content-between mb-1">
                    <span>
                      <IntlMessages id="shopee.workspace.finance.meta.bank-account" />
                    </span>
                    <strong className="text-dark">
                      <IdOrText value={selectedSettlement.bankAccount} />
                    </strong>
                  </div>
                  <div className="d-flex justify-content-between mb-1">
                    <span>
                      <IntlMessages id="shopee.workspace.finance.meta.transaction-code" />
                    </span>
                    <strong className="text-dark">
                      {selectedSettlement.transactionCode}
                    </strong>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>
                      <IntlMessages id="shopee.workspace.finance.meta.status" />
                    </span>
                    <strong className="text-success">
                      <IdOrText value={selectedSettlement.payoutStatusText} />
                    </strong>
                  </div>
                </div>

                {/* Sub tabs in detail panel */}
                <div
                  className="d-flex align-items-center border-bottom mb-3"
                  style={{ gap: 12 }}
                >
                  <Button
                    color="link"
                    className={`p-0 pb-2 text-decoration-none small ${
                      detailTab === "overview"
                        ? "font-weight-bold text-primary border-bottom border-primary"
                        : "text-muted"
                    }`}
                    style={{ borderRadius: 0 }}
                    onClick={() => setDetailTab("overview")}
                  >
                    <IntlMessages id="shopee.workspace.tab.overview" />
                  </Button>
                  <Button
                    color="link"
                    className={`p-0 pb-2 text-decoration-none small ${
                      detailTab === "orders"
                        ? "font-weight-bold text-primary border-bottom border-primary"
                        : "text-muted"
                    }`}
                    style={{ borderRadius: 0 }}
                    onClick={() => setDetailTab("orders")}
                  >
                    <IntlMessages
                      id="shopee.workspace.finance.tab-orders"
                      values={{ count: selectedSettlement.orderCount }}
                    />
                  </Button>
                  <Button
                    color="link"
                    className={`p-0 pb-2 text-decoration-none small ${
                      detailTab === "history"
                        ? "font-weight-bold text-primary border-bottom border-primary"
                        : "text-muted"
                    }`}
                    style={{ borderRadius: 0 }}
                    onClick={() => setDetailTab("history")}
                  >
                    <IntlMessages id="shopee.workspace.mapping.tab-history" />
                  </Button>
                </div>

                {/* Overview Breakdown List */}
                {detailTab === "overview" && (
                  <div>
                    <div className="mb-3" style={{ fontSize: 13 }}>
                      {/* Tổng doanh thu */}
                      <div className="d-flex justify-content-between align-items-center mb-2 font-weight-bold text-dark">
                        <span><IntlMessages id="shopee.workspace.finance.bd.gross-revenue" /></span>
                        <span>{money(selectedSettlement.grossAmount)}</span>
                      </div>

                      {/* Giảm trừ bối sàn */}
                      <div className="mb-2">
                        <div className="d-flex justify-content-between align-items-center font-weight-bold text-danger">
                          <span><IntlMessages id="shopee.workspace.finance.bd.platform-deduction" /></span>
                          <span>
                            {money(
                              selectedSettlement.platformDiscount || -8530000,
                            )}
                          </span>
                        </div>
                        <div className="pl-3 small text-muted">
                          <div className="d-flex justify-content-between my-1">
                            <span><IntlMessages id="shopee.workspace.finance.bd.voucher" /></span>
                            <span className="text-danger">
                              {money(selectedSettlement.voucher || -3150000)}
                            </span>
                          </div>
                          <div className="d-flex justify-content-between my-1">
                            <span><IntlMessages id="shopee.workspace.finance.bd.shipping-buyer" /></span>
                            <span className="text-danger">
                              {money(
                                selectedSettlement.shippingBuyer || -3280000,
                              )}
                            </span>
                          </div>
                          <div className="d-flex justify-content-between my-1">
                            <span><IntlMessages id="shopee.workspace.finance.bd.seller-discount" /></span>
                            <span className="text-danger">
                              {money(
                                selectedSettlement.sellerDiscount || -2100000,
                              )}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Phí & khấu trừ */}
                      <div className="mb-2">
                        <div className="d-flex justify-content-between align-items-center font-weight-bold text-danger">
                          <span><IntlMessages id="shopee.workspace.finance.kpi.fees" /> ^</span>
                          <span>{money(selectedSettlement.feeAmount)}</span>
                        </div>
                        <div className="pl-3 small text-muted">
                          <div className="d-flex justify-content-between my-1">
                            <span><IntlMessages id="shopee.workspace.finance.bd.payment-fee" /></span>
                            <span className="text-danger">
                              {money(selectedSettlement.paymentFee || -1870000)}
                            </span>
                          </div>
                          <div className="d-flex justify-content-between my-1">
                            <span><IntlMessages id="shopee.workspace.finance.bd.service-fee" /></span>
                            <span className="text-danger">
                              {money(selectedSettlement.serviceFee || -2310000)}
                            </span>
                          </div>
                          <div className="d-flex justify-content-between my-1">
                            <span><IntlMessages id="shopee.workspace.finance.bd.tax" /></span>
                            <span className="text-danger">
                              {money(selectedSettlement.tax || -1050000)}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Hoàn tiền / Trả hàng */}
                      <div className="mb-2">
                        <div className="d-flex justify-content-between align-items-center font-weight-bold text-danger">
                          <span><IntlMessages id="shopee.workspace.finance.bd.refunds" /></span>
                          <span>{money(selectedSettlement.refundAmount)}</span>
                        </div>
                        <div className="pl-3 small text-muted">
                          <div className="d-flex justify-content-between my-1">
                            <span><IntlMessages id="shopee.workspace.finance.kpi.refund" /></span>
                            <span className="text-danger">
                              {money(selectedSettlement.itemRefund || -1850000)}
                            </span>
                          </div>
                          <div className="d-flex justify-content-between my-1">
                            <span><IntlMessages id="shopee.workspace.finance.bd.refund-fee" /></span>
                            <span className="text-danger">
                              {money(selectedSettlement.refundFee || -300000)}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* COD & Khấu trừ khác */}
                      <div className="d-flex justify-content-between align-items-center mb-1 text-muted">
                        <span>COD</span>
                        <span>0 đ</span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center mb-3 text-muted">
                        <span><IntlMessages id="shopee.workspace.finance.bd.other-deductions" /></span>
                        <span>-0 đ</span>
                      </div>
                    </div>

                    {/* Highlight Payout Box */}
                    <div
                      className="p-3 rounded mb-3 d-flex justify-content-between align-items-center"
                      style={{
                        backgroundColor: "#f0fdf4",
                        border: "1px solid #bbf7d0",
                      }}
                    >
                      <span
                        className="font-weight-bold text-dark"
                        style={{ fontSize: 15 }}
                      >
                        <IntlMessages id="shopee.workspace.finance.net-received" />
                      </span>
                      <h4
                        className="font-weight-bold text-success mb-0"
                        style={{ fontSize: 20 }}
                      >
                        {money(selectedSettlement.netPayout)}
                      </h4>
                    </div>

                    {/* Action buttons */}
                    <div
                      className="d-flex justify-content-between mb-3"
                      style={{ gap: 8 }}
                    >
                      <Button
                        outline
                        color="secondary"
                        size="sm"
                        className="w-50 bg-white border text-dark font-weight-bold"
                        style={{ fontSize: 11, borderRadius: 6 }}
                      >
                        📄 <IntlMessages id="shopee.workspace.finance.action.view-documents" />
                      </Button>
                      <Button
                        outline
                        color="secondary"
                        size="sm"
                        className="w-50 bg-white border text-dark font-weight-bold"
                        style={{ fontSize: 11, borderRadius: 6 }}
                      >
                        📥 <IntlMessages id="shopee.workspace.finance.action.download-statement" />
                      </Button>
                    </div>

                    {/* Discrepancy Warning Alert if applicable */}
                    {selectedSettlement.discrepancyAmount > 0 && (
                      <div
                        className="p-3 rounded"
                        style={{
                          backgroundColor: "#fffbe6",
                          border: "1px solid #ffe58f",
                        }}
                      >
                        <div
                          className="d-flex align-items-center mb-1 text-warning font-weight-bold"
                          style={{ color: "#d48806" }}
                        >
                          <span className="mr-1">⚠️</span>{" "}
                          <IntlMessages id="shopee.workspace.finance.discrepancy.alert" />{" "}
                          <span className="ml-1 text-danger">
                            {money(selectedSettlement.discrepancyAmount)}
                          </span>
                        </div>
                        <p
                          className="small text-muted mb-2"
                          style={{ fontSize: 11 }}
                        >
                          <IntlMessages
                            id="shopee.workspace.finance.discrepancy.desc"
                            values={{
                              count: selectedSettlement.discrepancyOrdersCount,
                            }}
                          />
                        </p>
                        <Button
                          color="link"
                          className="p-0 small font-weight-bold text-primary text-decoration-none"
                        >
                          <IntlMessages id="shopee.workspace.returns.view-detail-title" />
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        )}
      </Row>

      {/* 7. Bottom Analytics Dashboard (4 Widgets Row) */}
      <Row className="mb-4">
        {/* Widget 1: Success Rate Donut */}
        <Col xs="12" md="6" lg="3" className="mb-3 mb-lg-0">
          <Card
            className="border-0 shadow-sm h-100"
            style={{ borderRadius: 10 }}
          >
            <CardBody className="p-3">
              <h6
                className="font-weight-bold text-dark mb-3"
                style={{ fontSize: 14 }}
              >
                <IntlMessages id="shopee.workspace.finance.widget.success-rate" />
              </h6>
              <div className="d-flex align-items-center justify-content-around my-2">
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center text-center font-weight-bold"
                  style={{
                    width: 110,
                    height: 110,
                    background:
                      "conic-gradient(#10b981 0% 96.7%, #f87171 96.7% 99.2%, #dc2626 99.2% 100%)",
                    position: "relative",
                  }}
                >
                  <div
                    className="rounded-circle bg-white d-flex flex-column align-items-center justify-content-center"
                    style={{ width: 75, height: 75 }}
                  >
                    <span
                      className="font-weight-bold text-dark"
                      style={{ fontSize: 16 }}
                    >
                      96.7%
                    </span>
                  </div>
                </div>

                <div className="small">
                  <div className="mb-1">
                    <span className="text-success font-weight-bold">●</span>{" "}
                    <IntlMessages id="shopee.workspace.finance.legend.success" />{" "}
                    <strong>96.7%</strong> (124)
                  </div>
                  <div className="mb-1">
                    <span className="text-warning font-weight-bold">●</span>{" "}
                    <IntlMessages id="shopee.workspace.finance.status.discrepancy" />:{" "}
                    <strong>2.5%</strong> (3)
                  </div>
                  <div>
                    <span className="text-danger font-weight-bold">●</span>{" "}
                    <IntlMessages id="shopee.workspace.finance.legend.failed" />{" "}
                    <strong>0.8%</strong> (1)
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>

        {/* Widget 2: Highest Revenue Channels */}
        <Col xs="12" md="6" lg="3" className="mb-3 mb-lg-0">
          <Card
            className="border-0 shadow-sm h-100"
            style={{ borderRadius: 10 }}
          >
            <CardBody className="p-3">
              <h6
                className="font-weight-bold text-dark mb-3"
                style={{ fontSize: 14 }}
              >
                <IntlMessages id="shopee.workspace.finance.widget.top-revenue-channels" />
              </h6>
              <div className="small">
                {[
                  {
                    name: "Shopee",
                    icon: "S",
                    bg: "#ee4d2d",
                    val: "680.450.000 đ",
                    pct: "54.1%",
                  },
                  {
                    name: "TikTok Shop",
                    icon: "🎵",
                    bg: "#111",
                    val: "298.350.000 đ",
                    pct: "23.7%",
                  },
                  {
                    name: "Lazada",
                    icon: "L",
                    bg: "#0f146d",
                    val: "185.200.000 đ",
                    pct: "14.7%",
                  },
                  {
                    name: "Website",
                    icon: "🌐",
                    bg: "#0284c7",
                    val: "60.450.000 đ",
                    pct: "4.8%",
                  },
                  {
                    nameId: "shopee.workspace.finance.channel-others",
                    name: "",
                    icon: "•",
                    bg: "#94a3b8",
                    val: "33.550.000 đ",
                    pct: "2.7%",
                  },
                ].map((item, idx) => (
                  <div key={idx} className="mb-2">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <div className="d-flex align-items-center">
                        <span
                          className="mr-2"
                          style={{
                            width: 16,
                            height: 16,
                            borderRadius: 3,
                            backgroundColor: item.bg,
                            color: "#fff",
                            fontSize: 9,
                            fontWeight: "bold",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {item.icon}
                        </span>
                        <span className="font-weight-bold text-dark">
                          {item.nameId ? (
                            <IntlMessages id={item.nameId} />
                          ) : (
                            item.name
                          )}
                        </span>
                      </div>
                      <div>
                        <strong className="mr-1 text-dark">{item.val}</strong>
                        <span className="text-muted">{item.pct}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>
        </Col>

        {/* Widget 3: Popular Refund Reasons */}
        <Col xs="12" md="6" lg="3" className="mb-3 mb-lg-0">
          <Card
            className="border-0 shadow-sm h-100"
            style={{ borderRadius: 10 }}
          >
            <CardBody className="p-3">
              <h6
                className="font-weight-bold text-dark mb-3"
                style={{ fontSize: 14 }}
              >
                <IntlMessages id="shopee.workspace.finance.widget.popular-refund-reasons" />
              </h6>
              <div className="d-flex align-items-center justify-content-around my-2">
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center text-center font-weight-bold"
                  style={{
                    width: 110,
                    height: 110,
                    background:
                      "conic-gradient(#0066FE 0% 38%, #38bdf8 38% 59%, #f59e0b 59% 77%, #ef4444 77% 92%, #94a3b8 92% 100%)",
                    position: "relative",
                  }}
                >
                  <div
                    className="rounded-circle bg-white d-flex flex-column align-items-center justify-content-center"
                    style={{ width: 75, height: 75 }}
                  >
                    <span className="small text-muted" style={{ fontSize: 9 }}>
                      <IntlMessages id="shopee.workspace.common.total" />
                    </span>
                    <span
                      className="font-weight-bold text-dark"
                      style={{ fontSize: 14 }}
                    >
                      312
                    </span>
                  </div>
                </div>

                <div className="small" style={{ fontSize: 11 }}>
                  <div className="mb-1">
                    <span className="text-primary font-weight-bold">●</span>{" "}
                    <IntlMessages id="shopee.workspace.finance.reason.wrong-color" />{" "}
                    <strong>38%</strong> (118)
                  </div>
                  <div className="mb-1">
                    <span className="text-info font-weight-bold">●</span>{" "}
                    <IntlMessages id="shopee.workspace.finance.reason.wrong-size" />{" "}
                    <strong>21%</strong> (65)
                  </div>
                  <div className="mb-1">
                    <span className="text-warning font-weight-bold">●</span>{" "}
                    <IntlMessages id="shopee.workspace.finance.reason.defect" />{" "}
                    <strong>18%</strong> (56)
                  </div>
                  <div className="mb-1">
                    <span className="text-danger font-weight-bold">●</span>{" "}
                    <IntlMessages id="shopee.workspace.finance.reason.not-liked" />{" "}
                    <strong>15%</strong> (47)
                  </div>
                  <div>
                    <span className="text-secondary font-weight-bold">●</span>{" "}
                    <IntlMessages id="shopee.workspace.returns.reason-other" />:{" "}
                    <strong>8%</strong> (26)
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>

        {/* Widget 4: AI Insight Box */}
        <Col xs="12" md="6" lg="3">
          <Card
            className="border-0 shadow-sm h-100"
            style={{ borderRadius: 10, backgroundColor: "#f0f7ff" }}
          >
            <CardBody className="p-3 d-flex flex-column justify-content-between">
              <div>
                <div className="d-flex align-items-center mb-2">
                  <span className="mr-1" style={{ fontSize: 16 }}>
                    🤖
                  </span>
                  <h6
                    className="font-weight-bold text-primary mb-0"
                    style={{ fontSize: 14 }}
                  >
                    AI Insight
                  </h6>
                </div>
                <div className="small text-dark" style={{ fontSize: 12 }}>
                  <div className="d-flex align-items-start mb-2">
                    <span className="text-success mr-2 font-weight-bold">
                      ●
                    </span>
                    <span>
                      <IntlMessages
                        id="shopee.workspace.finance.insight.unpaid-orders"
                        values={{ count: 12 }}
                      />
                    </span>
                  </div>
                  <div className="d-flex align-items-start mb-2">
                    <span className="text-success mr-2 font-weight-bold">
                      ●
                    </span>
                    <span>
                      <IntlMessages
                        id="shopee.workspace.finance.insight.transfer-tomorrow"
                        values={{ date: "25/07" }}
                      />
                    </span>
                  </div>
                  <div className="d-flex align-items-start">
                    <span className="text-success mr-2 font-weight-bold">
                      ●
                    </span>
                    <span>
                      <IntlMessages
                        id="shopee.workspace.finance.insight.fee-increase"
                        values={{ percent: "18%" }}
                      />
                    </span>
                  </div>
                </div>
              </div>
              <div className="pt-2">
                <Button
                  outline
                  color="primary"
                  size="sm"
                  className="w-100 font-weight-bold"
                  style={{ borderRadius: 6 }}
                >
                  <IntlMessages id="shopee.workspace.finance.insight.view-more" />
                </Button>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
});
